/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import styles from './styles';
import { Box, Checkbox, Grid, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { ContentCopyOutlined, DeleteRounded, EditOutlined, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { displacementTablecColumns } from './table_config';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import SelectEditInputTextField from '../../../ui/SelectEditInputTextField';
import SelectDrillDetails from '../../../ui/SelectDrillDetails';
import { COLORS } from '../../../../shared/config/colors';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import Datagrid from '../../../ui/Datagrid';
import { isMobile } from 'react-device-detect';
import MobileDisplacementRunInput from '../../../mobile/hydraulics/featuredWellRunInputs/displacementRunInputs';
import MobileSelectDrillDetails from '../../../mobile/hydraulics/SelectDrillDetails';
import MobileSelectEditInputTextField from '../../../mobile/hydraulics/SelectEditInputTextField';
import { CONTROL_TABLE_PARAMETER_MAP, DISPLACEMENT_STEP_SIZE_DIVISION_COUNT, HIDE_TARGET_ECD, MAX_ROW_LIMITS } from '../../../../utils/constants';
import WellboreTargetDialog from '../WellboreRunInputs/WellboreTargetDialog';
import MobileWellboreTargetDialog from '../../../mobile/hydraulics/targetDialog';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const DisplacementInputs = ({
  hydraulicsLanding,
  proxyLoader,
  displacementData,
  setDisplacementData,
  setParameterDirty,
  hydraulicsRunUnits
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const featuredWellRun = useSelector(state => state.hydraulicsLanding.featuredWellRun);
  const [deleteRow, setDeleteRow] = useState();
  const displacementUnits = hydraulicsLanding?.units?.displacement.inputs;
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [anchorPoint, setAnchorPoint] = useState('None');
  const [targetECD, setTargetECD] = useState('');
  const [targetData, setTargetData] = useState({});
  const [selectedRow, setSelectedRow] = useState({});
  const trackIdsRef = useRef([]);

  useEffect(() => {
    setDisplacementData(featuredWellRun?.inputs);
  }, [featuredWellRun]);

  useEffect(() => {
    if (displacementData?.length && !hydraulicsLanding?.loading) {
      const { drillFluids = [] } = hydraulicsLanding?.data || [];
      const units = hydraulicsLanding?.units?.drillFluids?.MD || { decimals: 2, label: 'ft' };
      const formatFluid = (id) => {
        const fluid = drillFluids.find((item) => item.fluid_id === id);
        return fluid
          ? `${fluid.name.trim()} @ ${fluid.MD.toFixed(units.decimals)}${units.label}`
          : 'N/A';
      };
      const updatedProperties = displacementData?.map((item, index) => ({
        ...item,
        fluid_id: item?.isNewRow
          ? ''
          : item?.fluid_id
            ? formatFluid(item.fluid_id)
            : 'N/A',
        row: index + 1,
        _id: generateRandom()
      }));
      const updateTrackFields = (data) => {
        return data.map((item) => ({
          ...item,
          track: item?.track ?? false,
        }));
      };
      const updatedPropertiesWithTrack = updateTrackFields(updatedProperties);
      setProperties(updatedPropertiesWithTrack);
      trackIdsRef.current = updatedPropertiesWithTrack.filter(item => item?.track).map(item => item._id);
    } else {
      setProperties([]);
    }
  }, [displacementData]);

  useEffect(() => {
    if (properties[0]?.from === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'flow_in', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'flow_in');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  useEffect(() => {
    if (targetData?.anchor_point && selectedRow?._id) {
      setProperties(prev =>
        prev.map(item =>
          item._id === selectedRow._id
            ? {
                ...item,
                target_data: targetData,
                isDirty: true
              }
            : item
        )
      );
    }
  }, [targetData]);

  const addNewDisplacementRow = () => {
    const nextId = properties.length;
    const newRow = {
      flow_in: '',
      fluid_id: '',
      fluid_volume: '',
      pumps_output: '',
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
      runNumber: properties.length + 1,
      step_size: 100,
      target_data: {
        anchor_point: 'None',
        target_ecd: null
      },
      track: properties.length === 0
    };
    setDisplacementData((prev) => [...prev, newRow]);
  };

  const transformRunsECDSpan = (targetData) => {
    if (!targetData.ecd_span) return [];
    return targetData?.ecd_span?.map((row) => ({
      name: row?.name,
      low_emw: Number(row.low_emw),
      high_emw: Number(row.high_emw),
    }));
  };

  const transformControlsTable = (targetData) => {
    if (!targetData.controls_table) return [];
    return targetData?.controls_table?.map((row) => ({
      maintain: row?.parameter?.toLowerCase() === 'sbp' ? '-' : row?.maintain?.toLowerCase(),
      parameter: CONTROL_TABLE_PARAMETER_MAP[row?.parameter] || row?.parameter,
      from: Number(row.from),
      to: Number(row.to),
      step_size: row?.parameter?.toLowerCase() === 'sbp' ? '-' : Number(row.step_size),
    }));
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: value,
            isDirty,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateDisplacementDetails = (details) => {
    const { _id, flow_in, fluid_volume, pumps_output, step_size } = details;
    const min_flow_in = displacementUnits.flow_in.min;
    const max_flow_in = displacementUnits.flow_in.max;
    const min_fluid_volume = displacementUnits.fluid_volume.min;
    const max_fluid_volume = 1000;
    const min_pumps_output = displacementUnits.pumps_output.min;
    const max_pumps_output = displacementUnits.pumps_output.max;
    const min_step_size = ((fluid_volume / pumps_output) / DISPLACEMENT_STEP_SIZE_DIVISION_COUNT.min);
    const max_step_size = ((fluid_volume / pumps_output) / DISPLACEMENT_STEP_SIZE_DIVISION_COUNT.max);

    if (flow_in <= min_flow_in || flow_in > max_flow_in) {
      return showError(`Flow Rate must be between ${min_flow_in} and ${max_flow_in} ${displacementUnits?.flow_in?.label}`, _id);
    }

    if (fluid_volume <= min_fluid_volume || fluid_volume > max_fluid_volume) {
      return showError(`Displacing Volume must be between ${min_fluid_volume} and ${max_fluid_volume} ${displacementUnits?.fluid_volume?.label}`, _id);
    }

    if (pumps_output <= min_pumps_output || pumps_output > max_pumps_output) {
      return showError(`Total Pumps Output must be between ${min_pumps_output} and ${max_pumps_output} ${displacementUnits?.pumps_output?.label}`, _id);
    }

    if (step_size <= min_step_size || step_size > max_step_size) {
      return showError(`Step Size must be between ${min_step_size.toFixed(0)} and ${max_step_size.toFixed(0)} ${displacementUnits?.step_size?.label}`, _id);
    }

    return true;
  };

  const checkDisplacementValues = (value) => {
    const row = value;
    const invalidFields = [];
    const isInvalidNumber = (val) => {
      // eslint-disable-next-line no-mixed-operators
      return isNaN(val) || val === '' || typeof val === 'string' && val.trim() === '';
    };

    if (row?.flow_in === undefined || row?.flow_in === null || isInvalidNumber(row.flow_in)) {
      invalidFields.push('Flow Rate');
    }
    if (row?.fluid_id === undefined || row?.fluid_id === null || row?.fluid_id === '') invalidFields.push('Displacing Fluid');

    if (row?.fluid_volume === undefined || row?.fluid_volume === null || isInvalidNumber(row.fluid_volume)) {
      invalidFields.push('Displacing Volume');
    }
    if (row?.pumps_output === undefined || row?.pumps_output === null || isInvalidNumber(row.pumps_output)) {
      invalidFields.push('Total Pumps Output');
    }
    if (row?.step_size === undefined || row?.step_size === null || isInvalidNumber(row.step_size)) {
      invalidFields.push('Step Size');
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateDisplacementDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveDisplacement = (row) => {
    if (checkDisplacementValues(row)) {
      let target_data;
      if (row?.target_data && Object.keys(row?.target_data).length !== 0) {
        const updatedRunsECDSpan = transformRunsECDSpan(row?.target_data);
        const updatedControlsTable = transformControlsTable(row?.target_data);
        target_data = {
          anchor_point: row?.target_data?.anchor_point,
          target_ecd: row?.target_data?.target_ecd == null
            ? null
            : Number(row?.target_data?.target_ecd),
          ecd_span: updatedRunsECDSpan,
          controls_table: updatedControlsTable
        };
      }
      const updated = properties.map((item) =>
        item._id === row._id
          ? { ...item, target_data, isNewRow: false, isDirty: false }
          : item
      );
      setProperties(updated);
      setDisplacementData(
        formatDisplacementData(
          updated,
          hydraulicsLanding?.data?.drillFluids,
          hydraulicsLanding?.units?.drillFluids
        )
      );
      setParameterDirty(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const updated = [...prev];
        updated[0] = { ...updated[0], isDirty: true };
        return updated;
      });
    }
  };

  const formatDisplacementData = (items, drillFluids, units) => {
    const getFluidId = (fluidLabel) => {
      const match = drillFluids?.find(
        (fluid) =>
          `${fluid?.name?.trim()} @ ${fluid?.MD.toFixed(units?.MD.decimals)}${units?.MD.label}` ===
          fluidLabel
      );
      return match?.fluid_id || null;
    };
    const formatItem = (item) => ({
      _id: item._id,
      flow_in: Number(item.flow_in),
      fluid_volume: Number(item.fluid_volume),
      pumps_output: Number(item.pumps_output),
      fluid_id: getFluidId(item?.fluid_id),
      step_size: Number(item.step_size),
      target_data: item?.target_data,
      track: item?.track ?? false
    });

    if (Array.isArray(items)) {
      return items.map(formatItem);
    }
    return formatItem(items);
  };

  const removeDisplacement = (row) => {
    setProperties((prev) => {
      const updated = prev.filter((item) => item._id !== row._id);
      setDisplacementData(
        formatDisplacementData(
          updated,
          hydraulicsLanding?.data?.drillFluids,
          hydraulicsLanding?.units?.drillFluids
        )
      );
      return updated;
    });
    setDeleteAction(false);
    setDeleteRow({});
    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], isDirty: true };
      return updated;
    });
  };

  const copyDisplacement = (row) => {
    setProperties((prev) => {
      const newRow = {
        ...row,
        track: false,
        row: prev.length + 1,
        _id: generateRandom(),
        isNewRow: true,
        isDirty: true,
      };
      const updated = [...prev, newRow];
      return updated;
    });
  };

  const renderActionCell = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || proxyLoader}
          onClick={() => {
            saveDisplacement(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={properties.some(item => item.isDirty === true || item.isNewRow || properties?.length >= MAX_ROW_LIMITS.DISPLACEMENT) || proxyLoader}
          onClick={() => {
            copyDisplacement(row);
          }}
        >
          <ContentCopyOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={proxyLoader}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderTargetsEditAction = ({ row }) => {
    return (
          <Grid container justifyContent="center">
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                  variant="solid"
                  onClick={() => {
                    if (row?.target_data?.anchor_point) {
                      setAnchorPoint(row?.target_data?.anchor_point);
                      setTargetECD(row?.target_data?.target_ecd);
                      setTargetData(row?.target_data);
                    }
                    setShowTargetModal(true);
                    setTimeout(() => {
                      setSelectedRow(row);
                    }, 100);
                  }}
                >
                  <EditOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
                {!row?.target_data?.anchor_point || HIDE_TARGET_ECD.includes(row?.target_data?.anchor_point)
                  ? (
                    <Typography fontSize={{ xs: '0.75rem', sm: '1rem' }} fontWeight={900}>None</Typography>
                    )
                  : (
                    <Box display="flex" flexDirection="column" textAlign='center'>
                      <Typography fontSize={{ xs: '0.75rem', sm: '1rem' }} lineHeight={1.2} fontWeight={900}>
                        {row?.target_data?.target_ecd} {hydraulicsRunUnits?.inputs?.target_data?.target_ecd?.label}
                      </Typography>
                      <Typography fontSize={{ xs: '0.75rem', sm: '1rem' }} lineHeight={1.2} fontWeight={900}>
                        @ {row?.target_data?.anchor_point}
                      </Typography>
                    </Box>
                    )}
              </Box>
            </Grid>
          </Grid>
    );
  };

  const handleAnchorPointTargetECD = (newAnchorPoint, newTargetECD) => {
    setAnchorPoint(newAnchorPoint);
    setTargetECD(newTargetECD);
  };

  const updateTargetData = (data) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === data?._id) {
          return {
            ...item,
            isDirty: true,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderTrackAction = ({ row }) => {
    const handleClick = (e) => {
      e.stopPropagation();

      const isChecked = e.target.checked;
      const isTracked = trackIdsRef.current.includes(row._id);
      const hasOtherTracked = properties.some(item => item.track);

      if (isChecked) {
        if (hasOtherTracked) {
          dispatch(
            enqueueSnackbar(
              'Only one row can get checked for tracking its fluid, please revise',
              'error',
              Date.now() + Math.random()
            )
          );
          return;
        }

        if (trackIdsRef.current.length && !isTracked) {
          dispatch(
            enqueueSnackbar(
              'Only one row can get checked for tracking its fluid, please save changes to proceed',
              'error',
              Date.now() + Math.random()
            )
          );
          return;
        }
      }

      updateData(row._id, 'track', isChecked);
    };

    return (
      <Box display="flex" justifyContent="center" width="100%">
        <Checkbox
          checked={!!row?.track}
          onClick={handleClick}
          disabled={row?.isNewRow}
        />
      </Box>
    );
  };

  const renderValueCell = (params) => {
    if (isMobile) {
      return (
        <MobileSelectEditInputTextField numberFormatOnly {...params} updateData={updateData} loading={hydraulicsLanding?.hydraulicsRunLoading} />
      );
    }
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderDrillFluidCell = (params) => {
    const drillFluids = hydraulicsLanding?.data?.drillFluids || [];
    const drillFluidsWithAir = [...drillFluids];
    if (isMobile) {
      return (
        <MobileSelectDrillDetails
          {...params}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={drillFluidsWithAir}
          label={hydraulicsLanding?.units?.drillFluids?.MD.label}
          decimals={hydraulicsLanding?.units?.drillFluids?.MD.decimals}
          loading={hydraulicsLanding?.hydraulicsRunLoading}
        />
      );
    }
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={drillFluidsWithAir}
        label={hydraulicsLanding?.units?.drillFluids?.MD.label}
        decimals={hydraulicsLanding?.units?.drillFluids?.MD.decimals}
      />
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <>
      <Grid container maxWidth={'1450px'}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" marginLeft={'13px'}>
            <Typography
              variant="button"
              fontWeight="700"
              onClick={addNewDisplacementRow}
              className={
                properties?.length >= MAX_ROW_LIMITS.DISPLACEMENT || properties.some(item => item.isDirty === true) || proxyLoader
                  ? classes.disableIcon
                  : ''
              }
              sx={{ cursor: 'pointer' }}
            >
              + Add
            </Typography>

            <Typography
              variant="h6"
              color={COLORS.LIGHT_GREY}
              fontWeight="500"
              fontSize={isMobile ? '15px' : '20px'}
              sx={{ marginLeft: '12px' }}
            >
              {`(Note: Maximum ${MAX_ROW_LIMITS.DISPLACEMENT} entries are allowed)`}
            </Typography>
          </Box>
        </Box>
        <Grid item xs={12}>
          {
            isMobile
              ? (
                <MobileDisplacementRunInput
                  data={properties}
                  columns={displacementTablecColumns(renderValueCell, renderDrillFluidCell, renderActionCell, displacementUnits, renderTargetsEditAction, renderTrackAction)}
                  updateData={updateData}
                  loading={proxyLoader}
                />
                )
              : (
                <Datagrid
                  apiRef={apiRef}
                  data={properties}
                  sx={{
                    marginLeft: '-6px',
                    '.MuiDataGrid-cell': {
                      fontSize: '1rem',
                      fontWeight: 900,
                      textAlign: 'justify',
                      pointerEvents: proxyLoader ? 'none' : 'auto',
                      backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
                    },
                    '.MuiDataGrid-columnHeader': {
                      fontSize: '1.2rem !important',
                      fontWeight: '900 !important'
                    },
                  }}
                  loading={proxyLoader}
                  columns={displacementTablecColumns(renderValueCell, renderDrillFluidCell, renderActionCell, displacementUnits, renderTargetsEditAction, renderTrackAction)}
                  onRowClick={(params, evt) => {
                    evt.preventDefault();
                    evt.stopPropagation();
                  }}
                  disableStickyHeader={false}
                  hideFooterPagination
                  hideFooterSelectedRowCount
                  autoHeight={false}
                  getRowId={(row) => row._id}
                  getRowHeight={() => 'auto'}
                  height={'24vh'}
                  getRowClassName={(params) => {
                    return invalidRows[params.id] ? classes.invalidRow : '';
                  }}
                  isRowSelectable={() => !proxyLoader}
                  pageSize={100}
                  editMode="cell"
                  toolbar={{
                    options: {
                      columns: false,
                      filters: false,
                      density: false,
                      export: false
                    },
                  }}
                  enableDirtyLock
                />
                )
          }
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeDisplacement}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      {showTargetModal
        ? <>
          {isMobile
            ? (
            <MobileWellboreTargetDialog
              updateTargetData={updateTargetData}
              data={featuredWellRun?.run_details}
              anchorPoint={anchorPoint}
              targetECD={targetECD}
              setTargetData={setTargetData}
              isOpen={showTargetModal}
              setAnchorPoint={setAnchorPoint}
              setTargetECD={setTargetECD}
              onSave={handleAnchorPointTargetECD}
              onCloseClick={() => {
                setAnchorPoint('None');
                setTargetECD(null);
                setShowTargetModal(false);
                setTimeout(() => {
                  setTargetData({});
                  setSelectedRow({});
                }, 100);
              }}
              hydraulicsRunUnits={hydraulicsRunUnits}
              targetData={targetData}
            />
              )
            : (
            <WellboreTargetDialog
              updateTargetData={updateTargetData}
              data={featuredWellRun?.run_details}
              anchorPoint={anchorPoint}
              targetECD={targetECD}
              setTargetData={setTargetData}
              isOpen={showTargetModal}
              setAnchorPoint={setAnchorPoint}
              setTargetECD={setTargetECD}
              onSave={handleAnchorPointTargetECD}
              onCloseClick={() => {
                setAnchorPoint('None');
                setTargetECD(null);
                setShowTargetModal(false);
                setTimeout(() => {
                  setTargetData({});
                  setSelectedRow({});
                }, 100);
              }}
              hydraulicsRunUnits={hydraulicsRunUnits}
              targetData={targetData}
            />
              )}
        </>
        : null
      }
    </>
  );
};
DisplacementInputs.propTypes = {
  hydraulicsLanding: PropTypes.object,
  proxyLoader: PropTypes.bool,
  displacementData: PropTypes.array,
  setDisplacementData: PropTypes.func,
  setParameterDirty: PropTypes.func,
  hydraulicsRunUnits: PropTypes.object
};

export default DisplacementInputs;
